import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Input, Label, FormGroup } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useGetTenantMarketingDefaults,
  useUpdateTenantMarketingDefaults,
} from "api/tenant-marketing-defaults/service";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const PROVIDERS = [
  { key: "cargurus", labelKey: "institution.internetMktgProviders.cargurus" },
  {
    key: "dealertiq_default_service",
    labelKey: "institution.internetMktgProviders.dealertiqDefaultService",
  },
];

const INTERNET_MKTG_TAB_STYLES = `
  body.white-content .institution-inventory-card__title {
    color: #1a1a1b;
  }
  body:not(.white-content) .institution-inventory-card__title {
    color: rgba(255, 255, 255, 0.88);
  }
  .internet-mktg-card .internet-mktg-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 22px;
  }
  .internet-mktg-card .institution-inventory-card__title {
    margin: 0;
    line-height: 1.2;
    display: flex;
    align-items: center;
  }
  .internet-mktg-card .form-check {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: 18px;
    width: 18px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .internet-mktg-card .form-check .form-check-label {
    position: relative;
    display: block;
    margin: 0;
    padding: 0;
    width: 18px;
    height: 18px;
    line-height: 18px;
  }
  .internet-mktg-card .form-check .form-check-sign::before,
  .internet-mktg-card .form-check .form-check-sign::after {
    top: 0 !important;
    left: 0 !important;
    margin: 0 !important;
  }
`;

const toBool = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const InternetMktgTab = ({ activeColorStyle, isTheme2 = false }) => {
  const { t } = useTranslation();
  const { data: record, isLoading } = useGetTenantMarketingDefaults();
  const updateMutation = useUpdateTenantMarketingDefaults();

  const [form, setForm] = useState({
    cargurus: false,
    dealertiq_default_service: false,
  });

  const titleFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : "14px";
  const emptyFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : "13px";

  useEffect(() => {
    if (!record) return;
    setForm({
      cargurus: toBool(record.cargurus),
      dealertiq_default_service: toBool(record.dealertiq_default_service),
    });
  }, [record]);

  const handleToggle = (key) => {
    const nextForm = { ...form, [key]: !form[key] };
    setForm(nextForm);

    updateMutation.mutate({
      cargurus: nextForm.cargurus,
      dealertiq_default_service: nextForm.dealertiq_default_service,
    });
  };

  const rows = useMemo(
    () =>
      PROVIDERS.map((provider) => ({
        ...provider,
        checked: !!form[provider.key],
      })),
    [form]
  );

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ color: activeColorStyle?.borderColor }}
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{INTERNET_MKTG_TAB_STYLES}</style>
      <div>
        {rows.map((row) => (
          <Card
            key={row.key}
            className="internet-mktg-card mb-2 pb-0 pt-0"
            style={{ overflow: "hidden", borderRadius: 0 }}
          >
            <CardBody className="py-2 px-3">
              <div className="internet-mktg-row">
                <h4
                  className="mb-0 font-weight-bold institution-inventory-card__title"
                  style={{ fontSize: titleFontSize }}
                >
                  {t(row.labelKey)}
                </h4>
                <FormGroup check className="mb-0">
                  <Label check className="mb-0">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id={`internet-mktg-${row.key}`}
                      name={row.key}
                      checked={row.checked}
                      disabled={updateMutation.isPending}
                      onChange={() => handleToggle(row.key)}
                    />
                    <span className="form-check-sign">
                      <span className="check" />
                    </span>
                  </Label>
                </FormGroup>
              </div>
            </CardBody>
          </Card>
        ))}
        {rows.length === 0 && (
          <div className="text-muted py-2 px-1" style={{ fontSize: emptyFontSize }}>
            {t("institution.internetMktgEmpty")}
          </div>
        )}
      </div>
    </>
  );
};

export default InternetMktgTab;
